import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Accueil', id: '/', path: '/' },
    { name: 'À Propos', id: 'about', path: '/about' },
    { name: 'Services', id: 'services', path: '/services' },
    { name: 'Public Cible', id: 'target', path: '/target' },
    { name: 'Contact', id: 'contact', path: '/contact' }
  ];

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/90 dark:bg-black/90 backdrop-blur-md border-b border-gray-200 dark:border-white/10' 
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-2 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="relative group">
              <img 
                src="/Fab43-Logo-lineaire.svg" 
                alt="Fab43" 
                className="h-40 w-40 text-gray-900 dark:text-white transition-all duration-300 relative z-10 group-hover:animate-pulse group-hover:scale-110"
                // Change h-40 w-40 to control size:
                // h-16 w-16 = 64px (smaller)
                // h-24 w-24 = 96px (current)
                // h-32 w-32 = 128px (larger)
                // h-40 w-40 = 160px (extra large)
              />
              <div className="absolute inset-0 bg-primary-500/20 dark:bg-white/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0">
                {/* INTENSE ELECTRIC SHOCK effect on hover */}
                <div className="absolute top-0 left-0 w-full h-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {/* Lightning bolts */}
                  <div className="absolute top-1/4 left-1/4 w-2 h-16 bg-gradient-to-b from-white via-yellow-300 to-blue-600 opacity-0 group-hover:opacity-100 animate-pulse shadow-lg shadow-yellow-400/50"></div>
                  <div className="absolute top-1/3 right-1/4 w-1.5 h-12 bg-gradient-to-b from-white via-yellow-300 to-blue-600 opacity-0 group-hover:opacity-100 animate-pulse shadow-lg shadow-yellow-400/50" style={{animationDelay: '0.2s'}}></div>
                  <div className="absolute bottom-1/4 left-1/3 w-1.5 h-20 bg-gradient-to-b from-white via-yellow-300 to-blue-600 opacity-0 group-hover:opacity-100 animate-pulse shadow-lg shadow-yellow-400/50" style={{animationDelay: '0.4s'}}></div>
                  <div className="absolute top-1/2 right-1/3 w-2 h-8 bg-gradient-to-b from-white via-yellow-300 to-blue-600 opacity-0 group-hover:opacity-100 animate-pulse shadow-lg shadow-yellow-400/50" style={{animationDelay: '0.6s'}}></div>
                  
                  {/* Electric sparks */}
                  <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100 animate-ping shadow-lg shadow-yellow-400/80"></div>
                    <div className="absolute top-1/2 right-1/4 w-4 h-4 bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100 animate-ping shadow-lg shadow-yellow-400/80" style={{animationDelay: '0.3s'}}></div>
                    <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100 animate-ping shadow-lg shadow-yellow-400/80" style={{animationDelay: '0.5s'}}></div>
                    <div className="absolute top-1/4 left-1/2 w-3 h-3 bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100 animate-ping shadow-lg shadow-yellow-400/80" style={{animationDelay: '0.7s'}}></div>
                    <div className="absolute top-1/3 right-1/2 w-2 h-2 bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100 animate-ping shadow-lg shadow-yellow-400/80" style={{animationDelay: '0.9s'}}></div>
                  </div>
                  
                  {/* Electric glow */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400/40 via-blue-400/30 to-purple-500/20 opacity-0 group-hover:opacity-100 animate-pulse shadow-lg shadow-yellow-400/60"></div>
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400/20 via-blue-400/15 to-purple-500/10 opacity-0 group-hover:opacity-100 animate-pulse shadow-lg shadow-yellow-400/60" style={{animationDelay: '0.1s'}}></div>
                </div>
              </div>
            </div>
          </div>
          
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className={`text-sm font-medium text-gray-700 dark:text-white/70 hover:text-gray-900 dark:hover:text-white transition-colors duration-300 relative group ${
                  location.pathname === item.path ? 'text-gray-900 dark:text-white' : ''
                }`}
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gray-900 dark:bg-white transition-all duration-300 group-hover:w-full"></span>
              </Link>
            ))}
            <ThemeToggle />
          </nav>

          <div className="md:hidden flex items-center space-x-4">
            <ThemeToggle />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-900 dark:text-white hover:rotate-90 transition-transform duration-300"
              aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <nav className="md:hidden mt-4 py-4 bg-white/90 dark:bg-black/90 backdrop-blur-md rounded-lg border border-gray-200 dark:border-white/10">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className={`block w-full text-left px-4 py-2 text-gray-700 dark:text-white/70 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white transition-all duration-300 ${
                  location.pathname === item.path ? 'text-gray-900 dark:text-white' : ''
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
